import { useMemo } from 'react';
import { Body1Strong, Caption1 } from '@fluentui/react-components';

import { getVersionId, TResolvedItemsMap } from '@modules/items';
import { ItemList, TDisplayItem } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';
import { TDocument, TDraft, TVersion } from '@modules/documents/types';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '@modules/inlineBlocks';

type TProps = {
  block: TDocument | TInlineBlock;
  version: TVersion | TDraft | TInlineBlockVersion | TInlineBlockDraft | undefined;
  resolvedItems: TResolvedItemsMap;
};

const NavBlockInsights = ({ block, version, resolvedItems }: TProps) => {
  const versionId = version ? getVersionId(version) : null;
  const isLatest = version && (version === block.draft || version === block.versions[0]); // both the draft and the last version are considered the latest for blocks only

  const displayItems: TDisplayItem[] = useMemo(() => {
    if (!versionId) return [];

    return block.placementsOfBlock
      .filter(
        (placedBlock) =>
          resolvedItems[placedBlock.doc] &&
          ((isLatest && placedBlock.versionMode === 'AUTO') || placedBlock.versionId === versionId)
      )
      .map(
        (placedBlock): TDisplayItem => ({
          id: placedBlock.id,
          duplicateId: placedBlock.doc,
          text: resolvedItems[placedBlock.doc].item.name,
          href: `/document/${placedBlock.doc}?view=latest&elem=${placedBlock.id}`,
        })
      );
  }, [isLatest, versionId, block.placementsOfBlock, resolvedItems]);

  if (!version) return <p className="pl-5">No draft or version found.</p>;

  return (
    <div className="mx-2">
      <div className="px-3 pt-4 pb-2">
        <p>
          <Body1Strong>
            {versionId !== 'draft' ? `Documents using v${versionId}` : 'Documents that will use this draft'}
          </Body1Strong>
        </p>
        {version && version === block.draft && (
          <p>
            <Caption1>({TERMINOLOGY.block.lower} is used with auto-versioning)</Caption1>
          </p>
        )}
      </div>

      <ItemList displayItems={displayItems} isSideBar />
    </div>
  );
};

export default NavBlockInsights;
